import React, { useState, useMemo } from "react";
import { Icon } from "@iconify/react";

const TableWithPagination = ({
  columns = [],
  data,
  extraFilters = {},
  searchPlaceholder = "Buscar...",
  onEdit,
  onDelete,
  onView,
  showView = true,
  showEdit = true,
  showDelete = true,
  renderExtraActions,
}) => {
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const filteredData = useMemo(() => {
    let filtered = data;

    if (search) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(search.toLowerCase())
        )
      );
    }

    Object.entries(extraFilters).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter((item) => item[key] === value);
      }
    });

    return filtered;
  }, [search, data, extraFilters]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key])
        return sortConfig.direction === "asc" ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key])
        return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div>
      {/* Contenedor flex para selector y búsqueda */}
      <div className="flex justify-between items-center mb-3 gap-4">
        {/* Selector de registros por página */}
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="itemsPerPage" className="text-slate-600">
            Registros por página:
          </label>
          <select
            id="itemsPerPage"
            className="border rounded px-2 py-1 text-sm"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 25, 50, 100].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
        </div>

        {/* Barra de búsqueda */}
        <input
          type="text"
          placeholder={searchPlaceholder}
          className="px-3 py-2 border rounded w-full max-w-xs"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <table className="min-w-full table-auto text-sm text-left text-slate-700">
        <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
          <tr>
            {columns.map((col, index) => (
              <th
                key={index}
                className="px-3 py-2 cursor-pointer select-none"
                onClick={() => requestSort(col.accessor)}
              >
                <div className="flex items-center gap-1">
                  {col.header}
                  {sortConfig.key === col.accessor && (
                    <Icon
                      icon={
                        sortConfig.direction === "asc"
                          ? "mdi:chevron-up"
                          : "mdi:chevron-down"
                      }
                      className="text-base"
                    />
                  )}
                </div>
              </th>
            ))}
            {(showView || showEdit || showDelete || renderExtraActions) && (
              <th className="px-3 py-2">Acciones</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {paginatedData.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + 1}
                className="text-center py-4 text-slate-400 text-sm"
              >
                No hay datos
              </td>
            </tr>
          )}
          {paginatedData.map((item, i) => (
            <tr key={i} className="hover:bg-slate-50 transition-colors">
              {columns.map((col, colIndex) => (
                <td key={colIndex} className="px-3 py-1.5">
                  {col.Cell
                    ? col.Cell({ row: item, value: item[col.accessor] })
                    : item[col.accessor]}
                </td>
              ))}
              {(showView || showEdit || showDelete || renderExtraActions) && (
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1">
                    {showView && (
                      <button
                        onClick={() => onView(item)}
                        className="flex items-center gap-1 px-1.5 py-0.5 border rounded text-green-700 bg-green-100 hover:bg-green-200 text-xs"
                      >
                        <Icon icon="mdi:eye-outline" className="text-base" />
                      </button>
                    )}
                    {showEdit && (
                      <button
                        onClick={() => onEdit(item)}
                        className="flex items-center gap-1 px-1.5 py-0.5 border rounded text-gray-700 bg-gray-100 hover:bg-gray-200 text-xs"
                      >
                        <Icon icon="mdi:pencil-outline" className="text-base" />
                      </button>
                    )}
                    {showDelete && (
                      <button
                        onClick={() => onDelete(item)}
                        className="flex items-center gap-1 px-1.5 py-0.5 border rounded text-red-700 bg-red-100 hover:bg-red-200 text-xs"
                      >
                        <Icon icon="mdi:trash-can-outline" className="text-base" />
                      </button>
                    )}
                    {renderExtraActions && renderExtraActions(item)}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>

          {/*
        <tbody className="divide-y divide-slate-200">
          {paginatedData.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + 1}
                className="text-center py-6 text-slate-400"
              >
                No hay datos
              </td>
            </tr>
          )}

          {paginatedData.map((item, i) => (
            <tr key={i} className="hover:bg-slate-100 transition-colors">
              {columns.map((col, colIndex) => (
                <td key={colIndex} className="px-6 py-4">
                  {col.Cell
                    ? col.Cell({ row: item, value: item[col.accessor] })
                    : item[col.accessor]}
                </td>
              ))}
              {(showView || showEdit || showDelete || renderExtraActions) && (
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {showView && (
                      <button
                        onClick={() => onView(item)}
                        className="flex items-center gap-1 px-2 py-1 border rounded text-green-700 bg-green-100 hover:bg-green-200"
                      >
                        <Icon icon="mdi:eye-outline" className="text-lg" />
                      </button>
                    )}
                    {showEdit && (
                      <button
                        onClick={() => onEdit(item)}
                        className="flex items-center gap-1 px-2 py-1 border rounded text-gray-700 bg-gray-100 hover:bg-gray-200"
                      >
                        <Icon icon="mdi:pencil-outline" className="text-lg" />
                      </button>
                    )}
                    {showDelete && (
                      <button
                        onClick={() => onDelete(item)}
                        className="flex items-center gap-1 px-2 py-1 border rounded text-red-700 bg-red-100 hover:bg-red-200"
                      >
                        <Icon icon="mdi:trash-can-outline" className="text-lg" />
                      </button>
                    )}
                    {renderExtraActions && renderExtraActions(item)}

                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody> */}
      </table>

      {/* Paginación */}
      <div className="flex justify-end items-center gap-1 mt-2">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded border hover:bg-gray-100 disabled:opacity-50"
        >
          <Icon icon="mdi:chevron-left" />
        </button>

        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`w-8 h-8 rounded border text-sm ${
              currentPage === page ? "bg-slate-800 text-white" : "bg-white"
            } hover:bg-gray-100`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded border hover:bg-gray-100 disabled:opacity-50"
        >
          <Icon icon="mdi:chevron-right" />
        </button>
      </div>
    </div>
  );
};

export default TableWithPagination;
